import React, { useState, useEffect, useContext } from "react";
import styled, { keyframes } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCommentDots, faTimes, faMicrophone } from "@fortawesome/free-solid-svg-icons";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import ISAFESound from "../../assets/sounds/ISAFEAI.mp3";
import LogoCircle from '../../assets/image/EdBrowser-logo-circle.png';
import ChatBg from '../../assets/image/edbrowser-chat-bg.png';
import { setAgeVerification } from "../../sagas/actions";
import { useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import API from "../../util/api";
import ReCaptchaV2 from "../Common/ReCaptchaV2";
import { ENABLE_CHATBOT_RECAPTCHA } from "../../util/constant";
import { showErrorModal } from "../../util/general";

const breakpoints = {
  phone: 480,
  tablet: 768,
};

// wavy dots animation
const dots = keyframes`
  0% { content: '.'; }
  33% { content: '..'; }
  66% { content: '...'; }
  100% { content: ''; }
`;

const ThinkingText = styled.span`
  &::after {
    content: '';
    animation: ${dots} 1.5s infinite;
    font-size: 1.5rem;
    color: ${({ colors }) => colors.darkBlue};
  }
`;

const ChatContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  z-index: 9999;

  @media (max-width: ${breakpoints.phone}px) {
    bottom: 10px;
    right: 10px;
  }
`;

const ChatbotIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  cursor: pointer;

  &:hover {
    background-color: ${({ colors }) => colors.darkBlue};
  }

  svg {
    color: ${({ colors }) => colors.myokBlue};
    font-size: 28px;
  }

  @media (max-width: ${breakpoints.phone}px) {
    width: 50px;
    height: 50px;
    svg { font-size: 24px; }
  }
`;

const Logo = styled.img`
  width: 32px;
  height: 32px;
  margin-right: 8px;
`;

const ChatbotDialog = styled.div`
  /* Dynamic sizing: cap by px, shrink to viewport when smaller */
  width: min(420px, 92vw);
  height: min(720px, 85vh);
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  /* Ensure internal scroll if content exceeds height */
  overflow: hidden;

  @media (max-width: ${breakpoints.phone}px) {
    position: fixed;
    bottom: 80px;
    right: 10px;
    left: 10px;
    width: auto;
    max-width: calc(100vw - 20px);
    height: auto;
    max-height: calc(100vh - 120px);
    padding: 12px;
    border-radius: 8px;
  }

  @media (max-width: ${breakpoints.tablet}px) and (min-width: ${breakpoints.phone + 1}px) {
    width: min(420px, 90vw);
    height: min(680px, 80vh);
  }
`;

const ChatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid ${({ colors }) => colors.myokBlue};
  padding-bottom: 10px;
`;

const ChatTitle = styled.h3`
  font-size: 1.5rem;
  color: ${({ colors }) => colors.darkBlue};
  margin: 0;

  @media (max-width: ${breakpoints.phone}px) {
    font-size: 1.2rem;
  }
`;

const CloseButton = styled.div`
  cursor: pointer;

  svg {
    color: ${({ colors }) => colors.darkBlue};
    font-size: 1.5rem;

    @media (max-width: ${breakpoints.phone}px) {
      font-size: 1.2rem;
    }
  }

  &:hover svg {
    color: ${({ colors }) => colors.myokBlue};
  }
`;

const ChatBody = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-height: 0;               
  overflow-y: auto;

  margin-top: 10px;
  padding: 10px;
  font-size: 1rem;
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-radius: 8px;
  background-color: ${({ colors }) => colors.lightBlue};

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: url(${ChatBg}) no-repeat center center;
    background-size: 300px;
    opacity: 0.1;
    pointer-events: none;
    z-index: 0;
  }
  & > * { position: relative; z-index: 1; }

  @media (max-width: ${breakpoints.phone}px) {
    margin-top: 8px;
    padding: 8px;
    font-size: 0.9rem;
    gap: 8px;
    &::before { background-size: 150px; }
  }
`;

const ChatInputContainer = styled.div`
  display: flex;
  align-items: center;
  padding-top: 10px;

  @media (max-width: ${breakpoints.phone}px) {
    flex-direction: row;
    gap: 8px;
    padding-top: 8px;
  }
`;

const ChatInput = styled.input`
  flex: 1;
  padding: 10px;
  border: 2px solid ${({ colors }) => colors.myokBlue};
  border-radius: 8px;
  font-size: 1rem;
  margin-right: 10px;
  background-color: ${({ disabled }) => (disabled ? "#e0e0e0" : "white")};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "text")};

  @media (max-width: ${breakpoints.phone}px) {
    width: 100%;
    margin-right: 0;
    padding: 8px;
    font-size: 0.9rem;
  }
`;

const MessageBubble = styled.div`
  max-width: 70%;
  padding: 10px 15px;
  border-radius: 15px;
  margin-bottom: 10px;
  color: ${({ sender, colors }) => (sender === "user" ? "white" : colors.darkBlue)};
  background-color: ${({ sender, colors }) =>
    sender === "user" ? colors.myokBlue : colors.lightBlue};
  align-self: ${({ sender }) => (sender === "user" ? "flex-end" : "flex-start")};
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  word-wrap: break-word;
  border-top-right-radius: ${({ sender }) => (sender === "user" ? "0" : "15px")};
  border-top-left-radius: ${({ sender }) => (sender === "user" ? "15px" : "0")};

  @media (max-width: ${breakpoints.phone}px) {
    max-width: 85%;
    padding: 8px 12px;
  }
`;

const SendButton = styled.button`
  padding: 10px 20px;
  background-color: ${({ colors, disabled }) => (disabled ? "#ccc" : colors.myokBlue)};
  color: white;
  border: none;
  border-radius: 8px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

  &:hover {
    background-color: ${({ colors, disabled }) => !disabled && colors.darkBlue};
  }

  @media (max-width: ${breakpoints.phone}px) {
    width: 20%;
    padding: 8px;
    font-size: 0.9rem;
  }
`;

const MicrophoneButton = styled.button`
  padding: 0;
  border: none;
  border-radius: 50%;
  color: ${({ colors, isListening }) => (isListening ? "#ff6347" : colors.myokBlue)};
  margin-right: 5px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  &:hover {
    color: ${({ colors, isListening, disabled }) =>
      disabled ? undefined : (isListening ? "#ff4500" : colors.darkBlue)};
  }

  @media (max-width: ${breakpoints.phone}px) {
    font-size: 1.2rem;
  }
`;

const customStyles = {
  control: (provided) => ({
    ...provided,
    borderColor: "#195174",
    marginBottom: "10px",
  }),
};

const generateOptions = (start, end, descending = false) => {
  let options = [];
  if (descending) {
    for (let i = start; i >= end; i--) {
      options.push({ value: i, label: i });
    }
  } else {
    for (let i = start; i <= end; i++) {
      options.push({ value: i, label: i });
    }
  }
  return options;
};


const useSpeechRecognition = () => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    if (!window.SpeechRecognition && !window.webkitSpeechRecognition) {
      // Suppress alerts; gracefully disable mic
      console.info("Speech Recognition API not supported; mic disabled.");
      setSupported(false);
      return;
    }

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.continuous = false; // Stop automatically after the user stops speaking
    recognition.interimResults = true;
    recognition.lang = "en-US";

    let isRecognitionActive = false;

    const handleStart = () => {
      // // console.log("Speech recognition started");
      isRecognitionActive = true;
    };

    const handleEnd = () => {
      // // console.log("Speech recognition ended");
      isRecognitionActive = false;
      setIsListening(false); // Reset listening state
    };

    const handleError = (event) => {
      console.warn("Speech recognition error:", event.error);
      isRecognitionActive = false;
      setIsListening(false);
      // alert(`Error: ${event.error}. Please try again.`);
    };

    const handleResult = (event) => {
      let interimText = "";
      let finalText = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcriptSegment = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalText += transcriptSegment;
        } else {
          interimText += transcriptSegment;
        }
      }

      // Process the transcripts to replace "I safe" with "ISAFE"
      const processedFinalText = replaceISAFE(finalText);
      const processedInterimText = replaceISAFE(interimText);

      setTranscript(processedFinalText); // Final results
      setInterimTranscript(processedInterimText); // Interim results

      // console.log("Interim transcript:", processedInterimText);
      // console.log("Final transcript:", processedFinalText);
    };

    // Function to replace "I safe" with "ISAFE"
    const replaceISAFE = (text) => {
      return text.replace(
        /(?:i safe|I safe|iSafe|I Safe|eye safe)/gi,
        "ISAFE"
      );
    };

    if (isListening && !isRecognitionActive) {
      recognition.start();
      recognition.onstart = handleStart;
      recognition.onresult = handleResult;
      recognition.onerror = handleError;
      recognition.onspeechend = handleEnd; // When speech ends
      recognition.onend = handleEnd; // When recognition service ends
    } else if (!isListening && isRecognitionActive) {
      recognition.stop();
      isRecognitionActive = false;
    }

    // Cleanup function to stop recognition when component unmounts
    return () => {
      if (isRecognitionActive) {
        recognition.stop();
        isRecognitionActive = false;
      }
    };
  }, [isListening]);

  return { isListening, setIsListening, transcript, interimTranscript, supported };
};

const EdBrowserChatbot = ({ colors }) => {
  const { isListening, setIsListening, transcript, interimTranscript, supported } = useSpeechRecognition();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [dob, setDob] = useState({ day: "", month: "", year: "" });
  const [ageVerified, setAgeVerified] = useState(false);
  const [captchaPassed, setCaptchaPassed] = useState(false);

  const dispatch = useDispatch();
  const signedIn = useSelector((state) => state.auth.signedIn);
  const notificationSound = new Audio(ISAFESound);
  const [sessionId, setSessionId] = useState(uuidv4());
  const token = useSelector((state) => state.auth.token);
  const age = useSelector(state => state.auth.user.age);
  const userRoleArray = useSelector((state) => state.auth.user.roles);
  const selected_role = useSelector((state) => state.auth.user.selected_role);
  const userName = useSelector((state) => state.auth.user.first);
  const user_id = useSelector((state) => state.auth.user.user_id);
  const customer_id = useSelector((state) => state.auth.user.customer_id);
  const location = useLocation();
  const [documentId, setDocumentId] = useState(null);
  const [timerStarted, setTimerStarted] = useState(false);
  const [isOnDocumentPage, setIsOnDocumentPage] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [isChatbotDisabled, setIsChatbotDisabled] = useState(false);
  // // console.log("Fetched Document Video Length: " + videoLength);
  const checkAIAccess = async () => {
    try {
      const response = await API.checkAIAccess({ user_id: user_id });
      // If access is not granted, disable the chatbot
      if (response?.authorized_to_use_ai == 0 || response?.authorized_to_use_ai?.authorized_to_use_ai == 0) {
        setIsChatbotDisabled(true); 
      } else {
        setIsChatbotDisabled(false);
      }
    } catch (error) {
      console.error("Error checking AI access:", error);
    }
  };

  // Call checkAIAccess on load
  useEffect(() => {
    if (selected_role == 7) {
      checkAIAccess(); 
    }
  }, [selected_role, user_id]);
  useEffect(() => {
    if (interimTranscript) {
      setInputValue(interimTranscript);
    }
  }, [interimTranscript]);

  const resetInactivityTimer = () => {
    setTimerStarted(false);
  };

  const handleVoiceInput = () => {
    setIsListening(!isListening);
  };

  const HTMLMessageBubble = ({ sender, text, colors, isThinking }) => (
    <MessageBubble sender={sender} colors={colors}>
      {isThinking ? (
        <ThinkingText colors={colors}>Thinking</ThinkingText>
      ) : sender === "bot" ? (
        stripHtmlTags(text)
      ) : (
        text
      )}
    </MessageBubble>
  );

  const stripHtmlTags = (html) => {
    if (html?.startsWith("```html")) {
      html = html.replace(/^```html/, "").trim();
    }

    if (html?.endsWith("```")) {
      html = html.replace(/```$/, "").trim();
    }

    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };
  
  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen && messages.length === 0) {
      setMessages([
        {
          sender: "bot",
          text: "Hello! I am EdBrowser AI, your ISAFE Digital Assistant. How can I assist you today?",
        },
      ]);
      notificationSound.play();
    }
  };
  const handleSendMessage = async () => {
    if (inputValue.trim()) {
      // console.log("User input:", inputValue);
      setMessages((prevMessages) => [
        ...prevMessages,
        { sender: "user", text: inputValue },
        { sender: "bot", isThinking: true },
      ]);
  
      const payload = {
        sessionId,
        text: inputValue,
        sessionState: {
          sessionAttributes: {
            signedIn: signedIn ? "true" : "false",
          },
        },
      };
  
      // Clear the input field immediately after sending.
      setInputValue("");
  
      try {
        const response = await API.getChatbotResponse(payload);
        // console.log("API response:", response);
  
        setMessages((prevMessages) => {
          const newMessages = [...prevMessages];
          if (newMessages[newMessages.length - 1].isThinking) {
            newMessages.pop();
          }
  
          let botResponseText;
  
          if (Array.isArray(response.response)) {
            if (response.response.length === 0) {
              botResponseText = "Sorry, I didn't get a response. Please try again.";
            } else {
              botResponseText = response.response
                .map((msg) => msg.content)
                .join("\n");
            }
          } else if (
            response &&
            response.response &&
            typeof response.response === "object" &&
            Object.keys(response.response).length === 0
          ) {
            botResponseText = "Sorry, I didn't get a response. Please try again.";
          } else {
            botResponseText = response.response;
          }
  
          return [
            ...newMessages,
            {
              sender: "bot",
              text: botResponseText,
            },
          ];
        });
        notificationSound.play();
      } catch (error) {
        console.error("API call error:", error);
        // Remove the thinking bubble and show an error message.
        setMessages((prevMessages) => {
          const newMessages = [...prevMessages];
          if (newMessages[newMessages.length - 1].isThinking) {
            newMessages.pop();
          }
          return [
            ...newMessages,
            {
              sender: "bot",
              text: "There was an error communicating with the bot. Please try again.",
            },
          ];
        });
        notificationSound.play();
      }
    }
  };
  

  const handleDobChange = (name, value) => {
    setDob((prev) => ({ ...prev, [name]: value }));
  };

  const verifyAge = () => {
    const { day, month, year } = dob;
    if (day && month && year) {
      if (ENABLE_CHATBOT_RECAPTCHA && !signedIn && !captchaPassed) {
        showErrorModal(
          "Please complete the reCAPTCHA before verifying your age."
        );
        return;
      }
      const dobDate = new Date(year, month - 1, day);
      const today = new Date();
      const age = today.getFullYear() - dobDate.getFullYear();
      if (
        age > 13 ||
        (age === 13 && today >= new Date(dobDate.setFullYear(today.getFullYear())))
      ) {
        setAgeVerified(true);
        dispatch(setAgeVerification(true)); // Save verification in Redux store
        setMessages([
          ...messages,
          {
            sender: "bot",
            text: "Thank you for verifying your age. How may I assist you?",
          },
        ]);
      } else {
        showErrorModal(
          "The EdBrowser AI chatbot is not available for students under the age of 13. \nPlease ask a parent, guardian, or teacher to help you!"
        );
      }
    } else {
      showErrorModal("Please fill in all fields for your Date of Birth.");
    }
  };

  const monthOptions = generateOptions(1, 12);
  const dayOptions = generateOptions(1, 31);
  const yearOptions = generateOptions(new Date().getFullYear(), new Date().getFullYear() - 105, true);

  useEffect(() => {
      setAgeVerified(signedIn);
      setCaptchaPassed(!!signedIn);
  }, [signedIn]);

  if (selected_role == 7 && (age < 13 || isChatbotDisabled)) {
    return null
  }
  return (
    <ChatContainer>
      {isOpen && (
        <ChatbotDialog colors={colors}>
          <ChatHeader colors={colors}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Logo src={LogoCircle} alt="EdBrowser logo" />
              <ChatTitle colors={colors}>EdBrowser AI</ChatTitle>
            </div>
            <CloseButton onClick={toggleChat} colors={colors}>
              <FontAwesomeIcon icon={faTimes} />
            </CloseButton>
          </ChatHeader>

          <ChatBody colors={colors}>
            {messages?.map((message, index) => (
              <HTMLMessageBubble
                key={index}
                sender={message.sender}
                text={message.text}
                colors={colors}
                isThinking={message.isThinking}
              />
            ))}

            {!ageVerified && (
              <div style={{ padding: "10px" }}>
                {ENABLE_CHATBOT_RECAPTCHA && !signedIn && !captchaPassed ? (
                  <>
                    <p>Please confirm you are not a robot.</p>
                    <div style={{ margin: "12px 0" }}>
                      <ReCaptchaV2
                        onVerify={() => setCaptchaPassed(true)}
                        onExpire={() => setCaptchaPassed(false)}
                        onError={() => setCaptchaPassed(false)}
                      />
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
                        Protected by reCAPTCHA. <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Privacy</a> • <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">Terms</a>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <p>Please verify your Date of Birth to proceed.</p>
                    <div style={{ display: "flex", gap: "10px" }}>
                      <Select
                        styles={customStyles}
                        options={dayOptions}
                        value={dayOptions?.find((option) => option.value === dob.day)}
                        onChange={(option) => handleDobChange("day", option.value)}
                        placeholder="Day"
                      />
                      <Select
                        styles={customStyles}
                        options={monthOptions}
                        value={monthOptions?.find((option) => option.value === dob.month)}
                        onChange={(option) => handleDobChange("month", option.value)}
                        placeholder="Month"
                      />
                      <Select
                        styles={customStyles}
                        options={yearOptions}
                        value={yearOptions?.find((option) => option.value === dob.year)}
                        onChange={(option) => handleDobChange("year", option.value)}
                        placeholder="Year"
                      />
                    </div>
                    <SendButton colors={colors} onClick={verifyAge}>
                      Verify Age
                    </SendButton>
                  </>
                )}
              </div>
            )}
          </ChatBody>

          <ChatInputContainer>
            <MicrophoneButton
              colors={colors}
              onClick={() => supported && handleVoiceInput()}
              isListening={isListening}
              disabled={!supported}
              title={supported ? "Voice input" : "Voice input not supported in this browser"}
            >
              <FontAwesomeIcon icon={faMicrophone} />
            </MicrophoneButton>
            <ChatInput
              colors={colors}
              type="text"
              placeholder="Type a message..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
              disabled={!ageVerified}
            />
            <SendButton colors={colors} onClick={handleSendMessage} disabled={!ageVerified}>
              Send
            </SendButton>
          </ChatInputContainer>
        </ChatbotDialog>
      )}

      <ChatbotIcon onClick={toggleChat} colors={colors}>
        <FontAwesomeIcon icon={faCommentDots} />
      </ChatbotIcon>
    </ChatContainer>
  );
};

export default EdBrowserChatbot;
